import PropTypes from 'prop-types';
import { memo } from 'react';
import { Text, useTheme } from '@Sage/hoi-poi-ui';
import BillboardBall from '../Ball';
import Tooltip from 'components/Tooltip';
import { UserChip } from 'containers/components/chips';
import { getLiteral } from 'utils/getLiteral';
import { getBillboardPercentage, getBillboardPercentageLabel } from '../utils';
import '../styles.scss';

const propTypes = {
    model: PropTypes.object.isRequired,
    containerReference: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    openSecondary: PropTypes.func,
};

const PrimaryPanelHeader = memo(({ model, containerReference, openSecondary }) => {
    const theme = useTheme();
    const openedPercentage = getBillboardPercentage(model, 'opened');
    const confirmedPercentage = getBillboardPercentage(model, 'confirmed');

    const renderGroupTooltip = (groups) => {
        if (!groups || groups.length === 0) return null;
        return (
            <Tooltip
                containerClassName="groups-text-more-tooltip"
                title={groups.join('\n')}
                position="top"
            >
                <Text
                    type="caption"
                    as="span"
                    className="groups-text-more"
                    color={theme.colors.utility.textSecondary}
                >
                    +{groups.length}
                </Text>
            </Tooltip>
        );
    };

    let groups = model.environments;
    let customGroups = model.customGroups;
    let groupsText = [];
    let groupsTextHidden = [];
    let count = 0;

    if (model.isMyTeam) {
        groupsText.push(getLiteral('label_billboard_team'));
        count++;
    }

    if (model.isMyCompany) {
        groupsText.push(getLiteral('label_billboard_company'));
        count++;
    }

    if (customGroups) {
        for (let customGroup of customGroups) {
            count++;
            if (count <= 3) {
                groupsText.push(customGroup.name);
            } else {
                groupsTextHidden.push(customGroup.name);
            }
        }
    }

    if (groups) {
        for (let group of groups) {
            if (group !== '') {
                count++;
                if (count <= 3) {
                    groupsText.push(group);
                } else {
                    groupsTextHidden.push(group);
                }
            }
        }
    }

    return (
        <div className="panel-primary-header" ref={containerReference}>
            <Text
                type="h5"
                bold
                as="div"
                className="title"
                color={theme.colors.utility.textPrimary}
            >
                {model.subject}
            </Text>
            <div className="balls">
                <BillboardBall
                    className={'ball-container sent'}
                    percentage={100}
                    strokeWidth={6}
                    literalLegend={'label_billboard_sent'}
                    onClick={() => openSecondary('sent')}
                >
                    <Text
                        type="h5"
                        bold
                        as="div"
                        className="ball-text-container-single"
                        color={theme.colors.utility.textPrimary}
                    >
                        {model.sent}
                    </Text>
                </BillboardBall>
                <BillboardBall
                    className={'ball-container opened'}
                    percentage={openedPercentage}
                    strokeWidth={6}
                    literalLegend={'label_billboard_opened'}
                    onClick={() => openSecondary('opened')}
                >
                    <div className="ball-text-container">
                        <Text
                            type="h5"
                            bold
                            as="div"
                            className="ball-text-top"
                            color={theme.colors.utility.textPrimary}
                        >
                            {model.opened}
                        </Text>
                        <div className="ball-text-separator" />
                        <Text
                            type="caption"
                            as="div"
                            className="ball-text-bottom"
                            color={theme.colors.utility.textSecondary}
                        >
                            {getBillboardPercentageLabel(model, 'opened')}
                        </Text>
                    </div>
                </BillboardBall>
                <BillboardBall
                    className={'ball-container confirmed'}
                    percentage={confirmedPercentage}
                    strokeWidth={6}
                    literalLegend={'label_billboard_status_confirmed'}
                    onClick={() => openSecondary('confirmed')}
                >
                    <div className="ball-text-container">
                        <Text
                            type="h5"
                            bold
                            as="div"
                            className="ball-text-top"
                            color={theme.colors.utility.textPrimary}
                        >
                            {model.confirmed}
                        </Text>
                        <div className="ball-text-separator" />
                        <Text
                            type="caption"
                            as="div"
                            className="ball-text-bottom"
                            color={theme.colors.utility.textSecondary}
                        >
                            {getBillboardPercentageLabel(model, 'confirmed')}
                        </Text>
                    </div>
                </BillboardBall>
            </div>
            <div className="info">
                <div className="from-to">
                    <div className="from-to-box">
                        <Text
                            type="caption"
                            as="span"
                            className="info-label"
                            color={theme.colors.utility.textSecondary}
                        >
                            {getLiteral('label_from')}
                        </Text>
                        <UserChip
                            idUser={model.idUser}
                            username={model.username}
                            remotePopover={true}
                        />
                        {model.startDateNotification && (
                            <Text
                                type="body"
                                as="div"
                                className="start-date"
                                color={theme.colors.utility.textPrimary}
                            >
                                {model.startDateNotification}
                            </Text>
                        )}
                    </div>
                    <div className="from-to-box">
                        <Text
                            type="caption"
                            as="span"
                            className="info-label"
                            color={theme.colors.utility.textSecondary}
                        >
                            {getLiteral('label_to')}
                        </Text>
                        <Text
                            type="body"
                            as="div"
                            className="groups-text"
                            color={theme.colors.utility.textPrimary}
                        >
                            {groupsText.join(', ')}
                        </Text>
                        {renderGroupTooltip(groupsTextHidden)}
                    </div>
                </div>
            </div>
        </div>
    );
});

PrimaryPanelHeader.propTypes = propTypes;

export default PrimaryPanelHeader;
